import { usePostHog } from "../../hooks/usePostHog";
import { type ClientName, composerLink, HELP_PROMPT, type OsCopy } from "./setupCopy";

interface SetupHelpProps {
    os: OsCopy;
    automatic: boolean;
    /** The assistant the user came from, when we could tell. */
    client: ClientName | null;
    /** Which card this instance sits on, for analytics. */
    card: string;
    /** Which journey this is, matching the flow that owns this help list. */
    source: string;
    userId?: string;
}

const CLIENTS: ClientName[] = [
    "ChatGPT",
    "Claude",
];

/**
 * "Need help?" — the single escape hatch, identical on every setup card.
 *
 * One consistent list: a symptom, one line, and a quiet button where there is a fix. The first row
 * hands the question to the user's own AI, because that is the only answer that works for someone
 * who does not know what a terminal is. Ported from the prototype's help list.
 */
const SetupHelp = ({ os, automatic, client, card, source, userId }: SetupHelpProps) => {
    const { track } = usePostHog();
    // One button when we know where they came from, both when we do not.
    const clients = client
        ? [
              client,
          ]
        : CLIENTS;

    return (
        <details
            className="rs-help"
            onToggle={(e) => {
                if ((e.currentTarget as HTMLDetailsElement).open) {
                    void track("onboarding_help_opened", {
                        user_id: userId,
                        card,
                        source,
                        detected_os: os.short,
                    });
                }
            }}
        >
            <summary>Need help?</summary>
            <div className="rs-helpbody">
                <div className="rs-helprow">
                    <b>Not sure what any of this means</b>
                    {client
                        ? `${client} can talk you through it, with the question already written.`
                        : "Your AI chat can talk you through it, with the question already written."}
                    {/* `offered` shadows nothing: `client` is the assistant we detected, which may
                        be null, while this is the one the button links to. */}
                    {clients.map((offered) => (
                        <a
                            key={offered}
                            className="rs-secondary"
                            href={composerLink(offered, HELP_PROMPT)}
                            target="_blank"
                            rel="noopener"
                            data-testid={`ask-${offered.toLowerCase()}`}
                            onClick={() =>
                                void track("onboarding_help_prompt_clicked", {
                                    user_id: userId,
                                    card,
                                    source,
                                    offered,
                                    client: client ?? "unknown",
                                })
                            }
                        >
                            Ask {offered}
                        </a>
                    ))}
                </div>

                {automatic && (
                    <div className="rs-helprow">
                        <b>Setup says Node.js needs an upgrade</b>
                        Quick setup installs Node.js only when it’s missing. If your existing version is too old, install Node.js 22.12 or newer
                        below, then open a new {os.term} window and run the setup command again.
                    </div>
                )}

                <div className="rs-helprow">
                    <b>
                        <code>{os.npxError}</code>
                    </b>
                    Node.js is missing. Install it, then open a new {os.term} window and paste the command again.
                    <a
                        className="rs-secondary"
                        href={os.nodeUrl}
                        data-testid="install-node"
                        onClick={() =>
                            void track("onboarding_install_node_clicked", {
                                user_id: userId,
                                card,
                                source,
                                detected_os: os.short,
                                arch: "x64",
                            })
                        }
                    >
                        Download Node.js for {os.short}
                    </a>
                    {os.nodeArmUrl ? (
                        <span className="rs-alt">
                            On {os.armLabel}?{" "}
                            {/* Tracked like its x64 sibling, with arch, or every ARM user who was
                                missing Node.js is invisible in the "why did they stall" breakdown. */}
                            <a
                                href={os.nodeArmUrl}
                                onClick={() =>
                                    void track("onboarding_install_node_clicked", {
                                        user_id: userId,
                                        card,
                                        source,
                                        detected_os: os.short,
                                        arch: "arm64",
                                    })
                                }
                            >
                                ARM build
                            </a>
                        </span>
                    ) : (
                        <span className="rs-alt">Works on Apple silicon and Intel</span>
                    )}
                </div>

                {os.short === "Windows" && (
                    <div className="rs-helprow">
                        <b>PowerShell says running scripts is disabled</b>
                        PowerShell is trying to run <code>npx.ps1</code>. Use <code>npx.cmd</code> instead. The copied Windows command already does
                        this; there is no need to change your execution policy.
                    </div>
                )}

                <div className="rs-helprow">
                    <b>Nothing happens after Enter</b>
                    If it asks <code>Ok to proceed? (y)</code>, press Enter again.
                </div>

                <div className="rs-helprow">
                    <b>No browser tab opened</b>
                    {os.term} prints a verification link. Copy it into your browser.
                </div>

                <div className="rs-helprow">
                    <b>Still stuck</b>
                    <a href="https://discord.gg/kQ27sNnZr7" target="_blank" rel="noopener">
                        Ask us on Discord
                    </a>
                </div>
            </div>
        </details>
    );
};

export default SetupHelp;
